// Charter requests list: counters, search, departure dates, Upcoming-Past and the table.
import { useLocation, useNavigate } from 'react-router-dom';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, DatePicker, Input, Segmented, Space, Statistic, Table, Tag, Typography } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';

import {
  dismissCharterRequestPaymentRequired,
  dismissCharterRequestQuoteRefresh,
  listCharters,
} from '../../api/endpoints';
import type { ColumnsType } from 'antd/es/table';
import type { CharterRequestRow, CharterRequestStatus } from '../../api/types';
import {
  CHARTER_REQUEST_ADHOC_COLORS,
  CHARTER_REQUEST_STATUS_COLORS,
  type CharterRequestAdhocFilter,
} from '../../data/statuses';
import { charterRequestColumns } from './charterRequestColumns';
import { useUrlParams } from '../../hooks/useUrlParams';

const { Title, Text } = Typography;

const COUNTERS: {
  key: keyof CharterRequestRow | 'total' | 'adhoc';
  label: string;
  status?: CharterRequestStatus;
  /** SHAR-1105/1116 — an ad-hoc label's card, a filter like Bookings' (SHAR-163). */
  adhoc?: CharterRequestAdhocFilter;
}[] = [
  { key: 'total', label: 'Total' },
  { key: 'status', label: 'Pending', status: 'pending' },
  // SHAR-1116 — immediately to the right of "Pending".
  { key: 'adhoc', label: 'Quote refresh', adhoc: 'quoteRefresh' },
  { key: 'status', label: 'Quotes received', status: 'quotes_received' },
  { key: 'status', label: 'Booked', status: 'booked' },
  { key: 'status', label: 'Cancelled', status: 'cancelled' },
  { key: 'adhoc', label: 'Payment required', adhoc: 'paymentRequired' },
];

export function ChartersListPage() {
  const nav = useNavigate();
  const loc = useLocation();
  // Admin regression 6 Oct — EVERY filter lives in the page address, as on
  // Charter bookings (SHAR-1256) and Bookings (useUrlParams): the status and
  // label cards, the search and the departure dates were component state, so
  // "Back" from a request mounted a fresh list and dropped them. Tab and page
  // were already in the URL (SHAR-849).
  const [sp, setParams] = useUrlParams();
  const status = (sp.get('status') || undefined) as CharterRequestStatus | undefined;
  // SHAR-1105/1116 — an ad-hoc label card's filter; one card is active at a time.
  const adhocParam = sp.get('adhoc');
  const adhoc: CharterRequestAdhocFilter | undefined =
    adhocParam === 'paymentRequired' || adhocParam === 'quoteRefresh' ? adhocParam : undefined;
  const setStatus = (v: CharterRequestStatus | undefined) => setParams({ status: v ?? null });
  const setAdhoc = (v: CharterRequestAdhocFilter | undefined) => setParams({ adhoc: v ?? null });
  const qc = useQueryClient();
  // SHAR-1105 — removing the label hides it in the admin only (PO, 29 Sep).
  const dismissPaymentRequired = useMutation({
    mutationFn: (id: string) => dismissCharterRequestPaymentRequired(id),
    // SHAR-1105 (retest) — the list AND the request's own page: the admin caches
    // every query for 30 s, so the other view kept showing the removed label.
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['charters'] });
      qc.invalidateQueries({ queryKey: ['charter', id] });
    },
  });
  // SHAR-1116 — the "Quote refresh" label, the same way (SHAR-1105 refresh).
  const dismissQuoteRefresh = useMutation({
    mutationFn: (id: string) => dismissCharterRequestQuoteRefresh(id),
    onSuccess: (_data, id) => {
      qc.invalidateQueries({ queryKey: ['charters'] });
      qc.invalidateQueries({ queryKey: ['charter', id] });
    },
  });
  // SHAR-579 — Upcoming / Past. Upcoming is the default: it is what an admin is
  // working on, and it is the side the counters describe.
  // SHAR-849 — the tab and the page live in the URL, not in component state.
  // A detail page cannot restore what the list never wrote down: "Back to
  // Charters" used to navigate to a bare /charters, which mounts fresh on
  // Upcoming page 1 however the admin got there. In the URL they survive the
  // round trip, a browser Back, a refresh and a pasted link.
  // useUrlParams merges the writes of one handler into ONE navigation, so
  // setWhen + setPage no longer race the way two setSearchParams calls did.
  const when = (sp.get('tab') === 'past' ? 'past' : 'upcoming') as 'upcoming' | 'past';
  const page = Math.max(1, Number(sp.get('page') ?? 1) || 1);
  const setWhen = (v: 'upcoming' | 'past') => setParams({ tab: v, page: null });
  const setPage = (v: number) => setParams({ page: v > 1 ? String(v) : null });
  const pageSize = 20;
  // SHAR-872 — the search box the Bookings page has.
  const q = sp.get('q') ?? '';
  const setQ = (v: string) => setParams({ q: v || null });

  // SHAR-110 — departure date range, inclusive, as the two strings the API takes.
  const range = { from: sp.get('from') || undefined, to: sp.get('to') || undefined };
  const setRange = (r: { from?: string; to?: string }) => setParams({ from: r.from ?? null, to: r.to ?? null });

  const list = useQuery({
    queryKey: ['charters', status, adhoc, q, page, when, range.from, range.to],
    queryFn: () =>
      listCharters({
        status,
        q: q || undefined,
        page,
        pageSize,
        when,
        from: range.from,
        to: range.to,
        paymentRequired: adhoc === 'paymentRequired' || undefined,
        quoteRefresh: adhoc === 'quoteRefresh' || undefined,
      }),
    // SHAR-845 — the counters and the rows come from this one response, so a
    // chip click changed the query key and React Query fell back to "no data
    // yet": the table blinked to its skeleton and every counter to zero for a
    // frame. Keeping the previous page on screen while the new one loads
    // removes the flash, and the counters are unaffected by the chip anyway
    // (the server groups them before the status filter), so what stays on
    // screen for that frame is already the right number.
    //
    // Bookings solves this with a SEPARATE counts query, which the ticket names
    // as the reference. There is no counts endpoint for charters, so copying
    // that shape here would mean a second round trip for numbers the list
    // already carries. Same outcome, one request.
    placeholderData: keepPreviousData,
  });

  const counts = list.data?.counts;

  // SHAR-801 (retest) — the columns (charterRequestColumns.tsx) and `scroll.x`,
  // DERIVED from them below rather than typed out again. Twice now this table has been
  // reported for squeezing or clipping columns, and both times the cause was
  // the same: a hand-written x that no longer matched the widths (1680 against
  // a real 1750), plus a column with no width at all to squeeze. A total that
  // is computed cannot fall out of step with the thing it totals.
  const columns: ColumnsType<CharterRequestRow> = charterRequestColumns({ nav, dismissPaymentRequired, dismissQuoteRefresh });
  const tableWidth = columns.reduce((n, c) => n + (Number((c as { width?: number }).width) || 0), 0);

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      <div>
        <Title level={3} style={{ margin: 0 }}>Charters</Title>
        <Text type="secondary">Charter requests and quotes.</Text>
      </div>

      {/* The counter strip is the one Bookings uses: antd Statistic inside a
          small hoverable Card, a fixed flex basis so the cards stay the same
          width whatever the label says, the loading state while the counts are
          in flight, and the active card marked by a border AND a ring rather
          than a border alone. This screen had a hand-rolled version of the same
          idea, which is why the cards used to change width with the label. */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          width: '100%',
        }}
      >
        {COUNTERS.map((c) => {
          const active = c.adhoc
            ? adhoc === c.adhoc
            : c.status
              ? status === c.status
              : !status && !adhoc;
          const value = c.adhoc
            ? (counts?.[c.adhoc] ?? 0)
            : c.status
              ? (counts?.[c.status] ?? 0)
              : (counts?.total ?? 0);
          return (
            <Card
              key={c.label}
              size="small"
              hoverable
              loading={list.isLoading}
              onClick={() => {
                setStatus(c.status);
                setAdhoc(c.adhoc);
                setPage(1);
              }}
              style={{
                // Regression sweep, 31 Aug: the status pill inside the card is
                // one line and does not shrink, so a long label ("Gathering
                // passengers") ran past the card's right edge at 130. The basis
                // fits the longest label the statuses can produce, and the pill
                // is allowed to wrap if a longer one is ever added.
                flex: '1 1 168px',
                minWidth: 168,
                cursor: 'pointer',
                borderColor: active ? '#1677ff' : undefined,
                boxShadow: active ? '0 0 0 1px #1677ff' : undefined,
              }}
              styles={{ body: { padding: '12px 16px' } }}
            >
              <Statistic
                title={
                  c.adhoc ? (
                    <Tag color={CHARTER_REQUEST_ADHOC_COLORS[c.adhoc]} style={{ marginInlineEnd: 0, whiteSpace: 'normal' }}>
                      {c.label}
                    </Tag>
                  ) : c.status ? (
                    <Tag color={CHARTER_REQUEST_STATUS_COLORS[c.status]} style={{ marginInlineEnd: 0, whiteSpace: 'normal' }}>
                      {c.label}
                    </Tag>
                  ) : (
                    c.label
                  )
                }
                value={value}
              />
            </Card>
          );
        })}
      </div>

      <Card>
        {/* SHAR-872 — the same toolbar as Bookings: search on the left,
            Upcoming/Past on the far right. The note that used to sit here said
            Charters had no search box "(not wanted)"; the ticket asks for one,
            and asks for it to match the Bookings implementation, which is what
            this is. */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            // SHAR-1039 — search and date range sit together on the left; only
            // the Upcoming/Past switch is pushed to the right (marginLeft auto).
            justifyContent: 'flex-start',
            gap: 16,
            flexWrap: 'wrap',
            marginBottom: 12,
          }}
        >
          <Input
            // re-created when the address's search changes (Back, browser
            // back/forward), so the box always shows the search the list is
            // filtered by
            key={q}
            prefix={<SearchOutlined />}
            placeholder="Search by request ID, user, route, flight ID, operator"
            defaultValue={q}
            onPressEnter={(e) => {
              setQ((e.target as HTMLInputElement).value);
              setPage(1);
            }}
            onChange={(e) => {
              if (!e.target.value) {
                setQ('');
                setPage(1);
              }
            }}
            style={{ flex: '1 1 320px', maxWidth: 480 }}
            allowClear
          />
          {/* SHAR-110 — the departure date range, beside the search it narrows.
              Both ends are inclusive and either may be left empty. */}
          <DatePicker.RangePicker
            allowEmpty={[true, true]}
            // shows the range the list is filtered by, also after Back or a reload
            value={[range.from ? dayjs(range.from) : null, range.to ? dayjs(range.to) : null]}
            format="DD/MM/YYYY"
            placeholder={['Departure from', 'Departure to']}
            onChange={(vals) => {
              const iso = (d: Dayjs | null | undefined) => (d ? d.format('YYYY-MM-DD') : undefined);
              setRange({ from: iso(vals?.[0]), to: iso(vals?.[1]) });
              setPage(1);
            }}
            // Admin regression 6 Oct — never narrower than its placeholders
            // ("Departure from" / "Departure to"), as on Bookings.
            style={{ flex: '0 1 300px', minWidth: 280 }}
          />
          <Segmented
            value={when}
            // SHAR-849 — setWhen resets the page itself.
            onChange={(v) => setWhen(v as 'upcoming' | 'past')}
            options={[
              { label: 'Upcoming', value: 'upcoming' },
              { label: 'Past', value: 'past' },
            ]}
            style={{ marginLeft: 'auto' }}
          />
        </div>
        <Table<CharterRequestRow>
          className="no-header-break"
          rowKey="id"
          loading={list.isLoading}
          dataSource={list.data?.data || []}
          onRow={(r) => ({
            // SHAR-849 — hand the detail page the exact list URL to come back to.
            onClick: () => nav(`/charters/${r.id}`, { state: { from: `${loc.pathname}${loc.search}` } }),
            style: { cursor: 'pointer' },
          })}
          /* Bookings scrolls horizontally rather than letting columns squash.
             This table gained six columns on 17 Aug and had neither this nor
             ellipsis, so on a narrow screen the route and the aircraft wrapped
             into several lines each.

             SHAR-801: x was 1500 while the columns added up to more, and some
             declared no width at all - so antd had room to "fit" the table by
             squeezing those, and their headers broke letter by letter
             ("De par tur e"). The retest found the same shape again, because the
             fix left two columns unsized and hard-coded an x that was 70 short of
             the real total. Both are now impossible: every column states a width
             and x is computed from them. */
          scroll={{ x: tableWidth }}
          pagination={{
            current: page,
            total: list.data?.total || 0,
            pageSize,
            onChange: setPage,
            showSizeChanger: false,
            showTotal: (t) => `${t} requests`,
          }}
          columns={columns}
        />
      </Card>
    </Space>
  );
}
